import '@lawpilots/design-system/styles.css'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { styled } from '@mui/system'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import history from 'history/browser'

import '@/locales/i18n'
import { store } from '@/store'
import { App, Login } from './common'
import { config } from './core/config'
import { setupTenant } from './core/tenant'
import { TenantData } from './core/types'
import { checkLDFeatureFlag } from './core/utils'
import reportWebVitals from './reportWebVitals'

const rootUrl = new URL(document?.URL)
const tenant = (await setupTenant(rootUrl)) as TenantData
const domain = rootUrl.hostname.replace('www.', '')
const subDomain = domain.split('.')[0]

// Render the top-level React component

const container = document.getElementById('root')
if (!container) {
  throw new Error('Root element not found')
}
const root = createRoot(container)

const BackgroundContainer = styled('div')({
  backgroundColor: '#F8F8FA',
  minHeight: '100vh',
})

const checkMaintenanceMode = async (): Promise<boolean> => {
  const context = {
    kind: 'app',
    key: 'laas-ui',
  }
  return checkLDFeatureFlag(context, 'maintenance-in-progress')
}

const initLaunchDarkly = async () => {
  if (config.app.env === 'local') {
    return await asyncWithLDProvider({
      clientSideID: config.launchDarkly.clientSideId,
      context: {
        kind: 'multi',
        app: {
          kind: 'app',
          key: 'laas-ui',
        },
        tenant: {
          kind: 'tenant',
          key: subDomain,
        },
      },
      flags: {
        'employee-id-config-enabled': true,
        'okta-integrations-enabled': true,
      },
      options: {
        streaming: config.launchDarkly.streaming,
        evaluationReasons: config.launchDarkly.evaluationReasons,
        sendEvents: config.launchDarkly.sendEvents,
      },
    })
  }

  try {
    return await asyncWithLDProvider({
      clientSideID: config.launchDarkly.clientSideId,
      context: {
        kind: 'multi',
        app: {
          kind: 'app',
          key: 'laas-ui',
        },
        tenant: {
          kind: 'tenant',
          key: subDomain,
        },
      },
      options: {
        streaming: true,
        sendEvents: true,
        evaluationReasons: true,
      },
    })
  } catch {
    return await asyncWithLDProvider({
      clientSideID: config.launchDarkly.clientSideId,
      flags: {
        'employee-id-config-enabled': false,
        'okta-integrations-enabled': false,
      },
      options: {
        streaming: false,
        sendEvents: false,
      },
    })
  }
}

// Check maintenance mode first with global app context
const isMaintenanceMode = await checkMaintenanceMode()

if (isMaintenanceMode) {
  // Render maintenance page without LaunchDarkly context
  const { MaintenancePage } = await import('./common/components/MaintenancePage')
  root.render(<MaintenancePage />)
} else {
  // Normal app initialization
  const LDProvider = await initLaunchDarkly()
  root.render(
    <Provider store={store}>
      <LDProvider>
        {!tenant.id ? (
          <BackgroundContainer>
            <Login />
          </BackgroundContainer>
        ) : (
          <App history={history} tenant={tenant} />
        )}
      </LDProvider>
    </Provider>,
  )
}

// To start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
